/**
 * Availability Type Configuration Registry
 *
 * Maps availability types to their default zone configurations.
 * These provide sensible defaults that journeys can override for journey-specific UX needs.
 */

import { AvailabilityType } from '../../booking/models/availabilityType';
import { AvailabilityTypePageConfig, JourneyZone } from '../models/checkout-journey.types';
import { BOOKING_AVAILABILITY_TYPE_CONFIG } from './availability-type-booking';
import { REQUEST_AVAILABILITY_TYPE_CONFIG } from './availability-type-request';
import { LIMITED_BOOKING_AVAILABILITY_TYPE_CONFIG } from './availability-type-limited-booking';

/**
 * Registry - Maps availability types to their page configurations
 */
export const AVAILABILITY_TYPE_CONFIGS: Record<AvailabilityType, AvailabilityTypePageConfig[]> = {
  BOOKING: BOOKING_AVAILABILITY_TYPE_CONFIG,
  REQUEST: REQUEST_AVAILABILITY_TYPE_CONFIG,
  'LIMITED-BOOKING': LIMITED_BOOKING_AVAILABILITY_TYPE_CONFIG,
};

/**
 * Get zone configuration for a specific availability type, page, and zone
 * Returns undefined if no config found for the combination
 */
export function getAvailabilityTypeZoneConfig<T>(
  availabilityType: AvailabilityType | null,
  pageRoute: string,
  zone: JourneyZone,
): T | undefined {
  if (!availabilityType) {
    return undefined;
  }

  const configs = AVAILABILITY_TYPE_CONFIGS[availabilityType];
  if (!configs) {
    console.warn(
      `[Availability Type Config] No configuration found for availability type: ${availabilityType}. Available types: ${Object.keys(AVAILABILITY_TYPE_CONFIGS).join(', ')}`,
    );
    return undefined;
  }

  const pageConfig = configs.find((config) => config.pageRoute === pageRoute);
  if (!pageConfig?.zoneConfig) {
    return undefined;
  }

  return pageConfig.zoneConfig[zone] as T | undefined;
}
